import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Sofa, Layers, BedDouble, Lightbulb, Snowflake, Home } from 'lucide-react';

const ServicesPage = () => {
  const services = [
    {
      icon: Sofa,
      title: 'Living Room Decoration',
      description:
        'Transform your main gathering space with curated furniture arrangements, color schemes, and accent pieces that create warmth and sophistication. We focus on creating functional layouts that encourage conversation while reflecting your personal style.',
      features: [
        'Furniture arrangement optimization',
        'Color palette development',
        'Accent piece selection',
        'Lighting design',
        'Art and accessory curation',
      ],
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: Layers,
      title: 'Accent Wall & Wallpaper Styling',
      description:
        'Make a bold statement with thoughtfully designed feature walls using premium wallpapers, textured finishes, or artistic paint techniques. Our designers help you choose the perfect focal point for any room.',
      features: [
        'Wallpaper selection and installation',
        'Custom paint finishes',
        'Textured wall treatments',
        'Mural and artwork integration',
        'Color blocking designs',
      ],
      image: 'https://images.unsplash.com/photo-1615529328331-f8917597711f?auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: BedDouble,
      title: 'Bedroom Enhancements',
      description:
        'Create your personal sanctuary with carefully selected linens, lighting, and decor that promote relaxation and restful sleep. We design bedrooms that balance aesthetics with comfort.',
      features: [
        'Bedding and linen consultation',
        'Ambient lighting design',
        'Window treatment selection',
        'Nightstand and dresser styling',
        'Closet organization solutions',
      ],
      image: 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: Lightbulb,
      title: 'Lighting & Decor Consultation',
      description:
        'Optimize your spaces with strategic lighting plans and decorative elements that enhance ambiance and functionality. Good lighting transforms the mood and usability of every room.',
      features: [
        'Layered lighting plans',
        'Fixture selection',
        'Smart lighting integration',
        'Task and accent lighting',
        'Natural light optimization',
      ],
      image: 'https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: Snowflake,
      title: 'Seasonal Decoration Updates',
      description:
        'Keep your home fresh and inviting with seasonal styling updates that reflect the changing moods throughout the year. From holiday decor to seasonal color refreshes, we help your space evolve.',
      features: [
        'Holiday decoration planning',
        'Seasonal color updates',
        'Textile and pillow swaps',
        'Tabletop styling',
        'Outdoor space transitions',
      ],
      image: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80',
    },
    {
      icon: Home,
      title: 'Full Home Styling',
      description:
        'Comprehensive whole-home decoration services that create a cohesive design narrative throughout your space. Perfect for new homeowners or those seeking a complete refresh.',
      features: [
        'Whole-home design concept',
        'Room-by-room styling',
        'Flow and continuity planning',
        'Furniture and decor sourcing',
        'Project management',
      ],
      image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1200&q=80',
    },
  ];

  return (
    <div data-testid="services-page">
      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#FAF9F6]">
        <div className="container-main">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#C5A065] mb-4 block">
              Our Services
            </span>
            <h1
              data-testid="services-page-title"
              className="font-heading text-4xl md:text-5xl text-[#2D2D2D] mb-6"
            >
              Decoration Services Tailored to Your Needs
            </h1>
            <p className="text-[#6E6E6E] text-lg leading-relaxed">
              From subtle enhancements to complete transformations, we offer
              comprehensive decoration services designed to bring your vision to
              life.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="space-y-24">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-detail-${index}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="image-hover-zoom aspect-[4/3]">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-full bg-[#FAF9F6] flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-[#C5A065]" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl text-[#2D2D2D]">
                      {service.title}
                    </h2>
                  </div>

                  <p className="text-[#6E6E6E] leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <h3 className="font-medium text-[#2D2D2D] mb-4">
                    What's Included:
                  </h3>
                  <ul className="space-y-3 mb-8">
                    {service.features.map((feature, fIndex) => (
                      <li
                        key={fIndex}
                        className="flex items-center gap-3 text-[#6E6E6E]"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-[#C5A065]" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <Link to="/contact">
                    <Button className="btn-secondary">
                      Get Started <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#2D2D2D]">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-8">
            Schedule a free consultation and let's discuss how we can transform
            your space.
          </p>
          <Link to="/contact">
            <Button data-testid="services-cta-btn" className="btn-accent px-10">
              Book Free Consultation
            </Button>
          </Link>
        </div>
      </section>

      {/* Pricing Note */}
      <section className="py-8 bg-[#1a1a1a]">
        <div className="container-main text-center">
          <p className="text-gray-500 text-sm">
            Project scope and pricing vary based on room size, materials, and
            customization requirements. All estimates are provided in writing
            before project commencement.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
